import { Content, type FilledLinkToMediaField } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { Button, Text } from 'components/@tedui';
import PrismicConditionalLink from 'components/PRISMIC/PrismicConditionalLink';
import { PrismicNextImage } from 'components/PRISMIC/PrismicNextImage';
import ContentContainer from 'components/content-container';
import PauseIcon from 'icons/PauseIcon';
import Sound from 'icons/SoundIcon';
import cx from 'classnames';
import { useIsMobile } from 'lib/hooks/useIsBreakpointWidth/useIsMobile';
import PlayIcon from 'icons/PlayIcon';
import mixpanel from 'lib/analytics/mixpanel';
import { motion } from 'motion/react';
import { useRef, useState, type JSX } from 'react';
import { isIOS, isSafari } from 'react-device-detect';

/**
 * Props for `HeroTedEd`.
 */
export type HeroTedEdProps = SliceComponentProps<Content.HeroTedEdSlice>;

/**
 * Component for "HeroTedEd" Slices.
 */

// create component to animate the text
const TextAnimation = ({
  children,
  animationDelay = 0,
  className
}: {
  children: React.ReactNode;
  animationDelay?: number;
  className?: string;
}): JSX.Element => {
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate={'visible'}
      transition={{
        duration: 1.4,
        delay: 0.2 + animationDelay,
        ease: 'easeOut'
      }}
      variants={{
        hidden: { y: 60, opacity: 0 },
        visible: { y: 0, opacity: 1 }
      }}
    >
      {children}
    </motion.div>
  );
};

const HeroTedEd = ({ slice }: HeroTedEdProps): JSX.Element => {
  const isDefaultVariation = slice.variation === 'default';
  const { isMobile } = useIsMobile({
    checkDevice: false,
    breakPointType: 'tui',
    mobileMaxSize: 'sm'
  });

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const video = slice.primary.videoUrl as FilledLinkToMediaField;

  const isWebMVideo = video.url && video.url.includes('.webm');
  if (!isWebMVideo) {
    console.warn('Please upload a webm video file for the HeroTedEd slice');
  }

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
        return;
      }
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleMute = () => {
    if (videoRef.current) {
      if (isMuted) {
        // Unmute: Set volume back to 1
        videoRef.current.volume = 1;
        videoRef.current.muted = false;
        setIsMuted(false);
      } else {
        // Mute: Set volume to 0
        videoRef.current.volume = 0;
        videoRef.current.muted = true;
        setIsMuted(true);
      }
    }
  };

  const gradientClasses = cx(
    'absolute z-10 w-full bg-gradient-to-b from-transparent to-pureBlack',
    isDefaultVariation ? 'bottom-0 h-28' : '-bottom-2',
    isMobile ? 'h-6' : 'h-14'
  );

  const textAnimationClasses = cx('lg-tui:w-[808px]', {
    'mb-9 lg-tui:mb-8': isDefaultVariation
  });

  const textContainerClasses = cx(
    'relative z-10 mx-5 flex flex-col sm-tui:mx-10 xl-tui:mx-0',
    {
      '-mt-12 sm-tui:-mt-14 md-tui:-mt-16 lg-tui:-mt-20': isDefaultVariation,
      'mt-6': !isDefaultVariation
    }
  );

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-pureBlack pb-16 text-white md-tui:pb-20"
    >
      <div className="relative z-0 flex w-full max-w-[1280px] items-center md-tui:top-0 md-tui:z-[1] md-tui:m-auto">
        {isIOS || isSafari || !isWebMVideo ? (
          <PrismicNextImage field={slice.primary.videoImg} />
        ) : (
          <video autoPlay loop muted playsInline ref={videoRef}>
            <source src={video.url} type="video/webm" />
          </video>
        )}
        <div className={gradientClasses} />
      </div>
      <ContentContainer className="mx-auto flex max-w-7xl" noPadding>
        <div className={textContainerClasses}>
          <TextAnimation
            animationDelay={0.6}
            className="mb-6 flex items-center justify-between gap-10 lg-tui:mb-8 xl-tui:mb-12"
          >
            <Text
              variant="header1"
              weight="font-semibold"
              useNewTextStyles
              color={{ override: 'white' }}
            >
              {slice.primary.title}
            </Text>
            {isWebMVideo && !isIOS && !isSafari && (
              <div className="flex gap-3">
                <button onClick={handlePlayPause}>
                  {isPlaying ? (
                    <PauseIcon variant="default" className="h-8 w-8" />
                  ) : (
                    <PlayIcon variant="outlined" className="h-8 w-8" />
                  )}
                </button>
                {!isDefaultVariation && (
                  <button onClick={handleMute}>
                    {!isMuted ? (
                      <Sound variant="on" className="h-8 w-8" />
                    ) : (
                      <Sound variant="off" className="h-8 w-8" />
                    )}
                  </button>
                )}
              </div>
            )}
          </TextAnimation>
          <TextAnimation animationDelay={0.9} className={textAnimationClasses}>
            <Text
              variant="header2"
              useNewTextStyles
              color={{ override: 'white' }}
            >
              {slice.primary.sideText}
            </Text>
          </TextAnimation>

          {isDefaultVariation && slice.primary.buttonLabel && (
            <TextAnimation
              animationDelay={1.2}
              className="max-w-[120px] rounded-sm border-thin"
            >
              <div className="">
                <PrismicConditionalLink
                  field={slice.primary.buttonLink}
                  onClick={() => {
                    mixpanel.track('slice_click', {
                      current_url: window.location.href,
                      title: slice.primary.title
                        .toLowerCase()
                        .replace(' ', '_'),
                      side_title: '',
                      item_number: '',
                      link_type: 'button'
                    });
                  }}
                >
                  <Button
                    variant="tertiary"
                    text={slice.primary.buttonLabel}
                    isDarkBackground
                  />
                </PrismicConditionalLink>
              </div>
            </TextAnimation>
          )}
        </div>
      </ContentContainer>
    </section>
  );
};

export default HeroTedEd;
